// SPDX-License-Identifier: AGPL-3.0-or-later

import {getProtectedLocalStorage, getProtectedSessionStorage} from '@app/features/platform/state/ProtectedWebStorage';

// The React SDK has no storage adapter option and uses browser globals internally.
// Its build seam receives only this SDK-scoped view, never Fluxer's raw backend.
const isPrivyKey = (key: string) => /^privy(?:[:_-]|$)/iu.test(key);
function scopedStorage(readBackend: () => Storage | null): Storage {
	const memory = new Map<string, string>();
	const keys = (): Array<string> => {
		const backend = readBackend();
		if (!backend) return [...memory.keys()];
		const result: Array<string> = [];
		for (let index = 0; index < backend.length; index++) {
			const key = backend.key(index);
			if (key && isPrivyKey(key)) result.push(key);
		}
		return result;
	};
	const getItem = (key: string): string | null =>
		isPrivyKey(String(key)) ? (readBackend()?.getItem(String(key)) ?? memory.get(String(key)) ?? null) : null;
	const setItem = (key: string, value: string): void => {
		if (!isPrivyKey(String(key))) throw new Error('Privy storage key is outside its namespace');
		const backend = readBackend();
		if (backend) backend.setItem(String(key), String(value));
		else memory.set(String(key), String(value));
	};
	const removeItem = (key: string): void => {
		if (!isPrivyKey(String(key))) return;
		readBackend()?.removeItem(String(key));
		memory.delete(String(key));
	};
	const methods = {
		getItem,
		setItem,
		removeItem,
		key: (index: number) => keys()[index] ?? null,
		clear: () => {
			for (const key of keys()) removeItem(key);
		},
	};
	return new Proxy(Object.create(null) as Storage, {
		get: (_target, key) =>
			key === 'length'
				? keys().length
				: key === Symbol.toStringTag
					? 'Storage'
					: typeof key === 'string' && Object.hasOwn(methods, key)
						? methods[key as keyof typeof methods]
						: typeof key === 'string'
							? (getItem(key) ?? undefined)
							: undefined,
		set: (_target, key, value) => {
			if (typeof key !== 'string') return false;
			setItem(key, String(value));
			return true;
		},
		deleteProperty: (_target, key) => {
			if (typeof key === 'string') removeItem(key);
			return true;
		},
		ownKeys: keys,
		getOwnPropertyDescriptor: (_target, key) =>
			typeof key === 'string' && isPrivyKey(key) && getItem(key) !== null
				? {configurable: true, enumerable: true, value: getItem(key), writable: true}
				: undefined,
	});
}
const local = scopedStorage(getProtectedLocalStorage);
const session = scopedStorage(getProtectedSessionStorage);
export const getPrivySdkLocalStorage = (): Storage => local;
export const getPrivySdkSessionStorage = (): Storage => session;
